// ─── M06 · Listas de negocio de VENTAS que la pantalla y la ruta leen de UN solo dueño · BUG-4182 ──────────────
//
// Antes cada lista vivía a mano en la pantalla, en la ruta y, a veces, en el tipo: quien agregara una opción tenía que
// acordarse de todos los lados, y la pantalla acababa ofreciendo algo que el servidor rechazaba (§25 REGLA 1).
// Está aparte del diccionario porque `erp-dictionary.ts` ya pasó de los 1000 renglones (regla R8).
// La base (`tenant-schema.ts`, `mysqlEnum`) sigue con su copia: no se migra aquí (deuda anotada en GAPS-I281).
//
// 🔴 ESPEJO BYTE A BYTE: `backend/src/types/catalogos-de-ventas.ts` (el build del back no compila `packages/`, igual que
// `erp-dictionary.ts`). Lo exige `bug-4182-las-listas-de-ventas-salen-del-dueno.test.ts`.

import { USUARIO_ROL, type UsuarioRol } from './erp-dictionary';

/** contratos.tipo_contrato — enum('compraventa','promesa','reserva','cesion') */
export const CONTRATO_TIPO = {
  COMPRAVENTA: 'compraventa',
  PROMESA:     'promesa',
  RESERVA:     'reserva',
  CESION:      'cesion',
} as const;
export type ContratoTipo = (typeof CONTRATO_TIPO)[keyof typeof CONTRATO_TIPO];

/** unidades.uso_destino — enum('venta','renta','administracion') */
export const UNIDAD_USO_DESTINO = {
  VENTA:          'venta',
  RENTA:          'renta',
  ADMINISTRACION: 'administracion',
} as const;
export type UnidadUsoDestino = (typeof UNIDAD_USO_DESTINO)[keyof typeof UNIDAD_USO_DESTINO];

/** prospecto_documentos.tipo_documento — enum de los papeles del cliente */
export const KYC_DOCUMENTO_TIPO = {
  INE_PASAPORTE:          'ine_pasaporte',
  RFC:                    'rfc',
  COMPROBANTE_DOMICILIO:  'comprobante_domicilio',
  ACTA_NACIMIENTO:        'acta_nacimiento',
  ESTADO_CIVIL:           'estado_civil',
  CURP:                   'curp',
  ESTADO_CUENTA_BANCARIO: 'estado_cuenta_bancario',
  CONSTANCIA_INGRESOS:    'constancia_ingresos',
  OTRO:                   'otro',
} as const;
export type KycDocumentoTipo = (typeof KYC_DOCUMENTO_TIPO)[keyof typeof KYC_DOCUMENTO_TIPO];

/** Los papeles que el SISTEMA pide para «expediente completo» (D-2026-09-11-L). */
export const KYC_PAPELES_REQUERIDOS = [
  KYC_DOCUMENTO_TIPO.INE_PASAPORTE,
  KYC_DOCUMENTO_TIPO.COMPROBANTE_DOMICILIO,
  KYC_DOCUMENTO_TIPO.CONSTANCIA_INGRESOS,
] as const;

/** Los que se pueden subir y no se piden (§7 del complemento de unidades y clientes). */
export const KYC_PAPELES_OPCIONALES = [
  KYC_DOCUMENTO_TIPO.RFC,
  KYC_DOCUMENTO_TIPO.ESTADO_CUENTA_BANCARIO,
] as const;

/** Los ids de los papeles que el VENDEDOR declara haber revisado (`kyc_declarado`, I-255). No son los de `KYC_DOCUMENTO_TIPO`. */
export const PAPEL_DECLARADO = {
  IDENTIFICACION: 'identificacion',
  DOMICILIO:      'domicilio',
  INGRESOS:       'ingresos',
  RFC:            'rfc',
  ESTADO_CUENTA:  'estado_cuenta',
} as const;

/** Los 3 que se exigen para FINALIZAR la venta; RFC y estado de cuenta se declaran pero no se exigen. */
export const PAPELES_DECLARADOS_REQUERIDOS = [
  PAPEL_DECLARADO.IDENTIFICACION,
  PAPEL_DECLARADO.DOMICILIO,
  PAPEL_DECLARADO.INGRESOS,
] as const;

/** cotizaciones.pagos_intermedios[].tipo — el valor del pago va como % del precio o como monto en pesos. */
export const PAGO_INTERMEDIO_TIPO = {
  PCT:   'pct',
  MONTO: 'monto',
} as const;
export type PagoIntermedioTipo = (typeof PAGO_INTERMEDIO_TIPO)[keyof typeof PAGO_INTERMEDIO_TIPO];

/** Qué se hace con lo que el cliente pagó de más que todo su plan (BUG-2235). */
export const SOBRANTE_ACCION = {
  DEVOLVER: 'devolver',
  A_FAVOR:  'a_favor',
} as const;
export type SobranteAccion = (typeof SOBRANTE_ACCION)[keyof typeof SOBRANTE_ACCION];

/** D-2026-10-27-C (Mau, 27-oct 12:23): quién puede quedar como vendedor de un prospecto. Lo exige el servidor (`usuario.service.ts`,
 *  `assertPuedeSerVendedor`, que llaman `actualizarProspecto` y `apartarProspectoConLock`) y la pantalla lo lee de aquí (`ReasignarVendedor.tsx`). */
const NOMBRE_DE_QUIEN_VENDE = { [USUARIO_ROL.VENDEDOR]: 'Vendedor', [USUARIO_ROL.GERENTE_VENTAS]: 'Gerente de ventas', [USUARIO_ROL.CFO]: 'CFO', [USUARIO_ROL.CEO]: 'CEO' } as const;
export const ROLES_QUE_VENDEN = Object.keys(NOMBRE_DE_QUIEN_VENDE) as readonly UsuarioRol[];
/** El aviso del freno, armado de la MISMA lista (ORQ 27-oct, revisión previa 🟡). */
export const AVISO_ROLES_QUE_VENDEN = `Sólo un ${Object.values(NOMBRE_DE_QUIEN_VENDE).slice(0, -1).join(', ')} o ${Object.values(NOMBRE_DE_QUIEN_VENDE).slice(-1)[0]} activo puede quedar como vendedor del prospecto.`;
